/**
 * Pricing rules for conditional pricing logic
 * Defines base formulas and conditions for each component type
 * Can be loaded from database or configuration
 *
 * Structure:
 * - baseFormula: Math expression for calculating cost (e.g., "unitPrice * quantity")
 * - conditions: Array of conditional adjustments (discount, surcharge, formula)
 * - Each condition has: if (condition), apply (type), value (adjustment)
 */

import { PricingRulesValidator } from './PricingRulesValidator';

export const DEFAULT_RULES = {
  doors: {
    alum: {
      conditions: [],
    },
    louver: {
      dulux: {
        conditions: [],
      },
      melamine: {
        conditions: [],
      },
    },
    milled: {
      dulux: {
        conditions: [],
      },
      melamine: {
        conditions: [],
      },
    },
    normal: {
      dulux: {
        conditions: [],
      },
    },
    acrylic: {
      conditions: [],
    },
    drilled: {
      dulux: {
        conditions: [],
      },
      melamine: {
        conditions: [],
      },
    },
    grainPano: {
      melamine: {
        conditions: [],
      },
    },
    glassInside: {
      conditions: [],
    },
    noGrainPano: {
      dulux: {
        conditions: [],
      },
      melamine: {
        conditions: [],
      },
    },
  },
  labor: {
    cnc: {
      conditions: [],
    },
    design: {
      conditions: [],
    },
    packing: {
      conditions: [],
    },
    assembly: {
      tv: {
        conditions: [],
      },
      bed: {
        conditions: [],
      },
      dry: {
        conditions: [],
      },
      led: {
        conditions: [],
      },
      desk: {
        conditions: [],
      },
      totl: {
        conditions: [],
      },
      shelf: {
        conditions: [],
      },
      vanity: {
        conditions: [],
      },
      bedLower: {
        conditions: [],
      },
      wardrobe: {
        conditions: [],
      },
      bedDrawer: {
        conditions: [],
      },
      shoeBench: {
        conditions: [],
      },
      decorative: {
        conditions: [],
      },
      shoeCabinet: {
        conditions: [],
      },
      kitchenLower: {
        conditions: [],
      },
      kitchenUpper: {
        conditions: [],
      },
      kitchenCeiling: {
        conditions: [],
      },
      wallPanelMirror: {
        conditions: [],
      },
      slatWallPartition: {
        conditions: [],
      },
      wallPanelMelamine: {
        conditions: [],
      },
    },
    painting: {
      conditions: [],
    },
    edgeBanding: {
      diff: {
        conditions: [],
      },
      bevel: {
        conditions: [],
      },
      normal: {
        conditions: [],
      },
    },
  },
  special: {
    backsplash: {
      conditions: [],
    },
    countertop: {
      conditions: [],
    },
    equipments: {
      conditions: [],
    },
    curveBlocks: {
      'rank1.AC.L': {
        conditions: [],
      },
      'rank1.AC.M': {
        conditions: [],
      },
      'rank2.AC.L': 1,
      'rank2.AC.M': {
        conditions: [],
      },
      'rank3.AC.L': {
        conditions: [],
      },
      'rank3.AC.M': {
        conditions: [],
      },
      'rank1.KES.L': {
        conditions: [],
      },
      'rank1.KES.M': {
        conditions: [],
      },
      'rank2.KES.L': {
        conditions: [],
      },
      'rank2.KES.M': {
        conditions: [],
      },
      'rank3.KES.L': {
        conditions: [],
      },
      'rank3.KES.M': {
        conditions: [],
      },
      'rank1.AC.Plastic': {
        conditions: [],
      },
      'rank2.AC.Plastic': {
        conditions: [],
      },
      'rank3.AC.Plastic': {
        conditions: [],
      },
      'rank1.ACC.Plywood': {
        conditions: [],
      },
      'rank2.ACC.Plywood': {
        conditions: [],
      },
      'rank3.ACC.Plywood': {
        conditions: [],
      },
    },
  },
  accessory: {
    leds: {
      conditions: [],
    },
    legs: {
      conditions: [],
    },
    handles: {
      conditions: [],
    },
    mirrors: {
      conditions: [],
    },
    ledsDecor: {
      conditions: [],
    },
    ledDrivers: {
      conditions: [],
    },
    lightSwitches: {
      conditions: [],
    },
    clothesHangers: {
      conditions: [],
    },
    standardLock: {
      conditions: [],
    },
  },
  component: {
    rails: {
      conditions: [],
    },
    hinges: {
      conditions: [],
    },
    liftSupport: {
      conditions: [],
    },
    hinges135: {
      conditions: [],
    },
    connectors: {
      conditions: [],
    },
  },
  materials: {
    boards: {
      conditions: [],
    },
    edgeBanding: {
      diff: {
        conditions: [],
      },
      bevel: {
        conditions: [],
      },
      normal: {
        conditions: [],
      },
    },
    acrylicCabinetTrims: {
      conditions: [],
    },
  },
  transport: {
    importDuty: {
      baseFormula: 'importDuty * grandSale',
      conditions: [],
    },
    shippingFee: {
      baseFormula: 'shippingFeeForeign * exchangeRate',
      baseFormulaWithProfit: 'cost / (1 - profit)',
      conditions: [],
    },
    groundTripFee: {
      baseFormula: 'groundTripForeign * exchangeRate',
      baseFormulaWithProfit: 'cost / (1 - profit)',
      conditions: [],
    },
  },
};

/**
 * Create a rule for a component type
 * @param {object} rule - Rule configuration
 * @returns {object} Validated rule
 */
export function createRule(rule) {
  if (!rule.baseFormula) {
    throw new Error('Rule must have baseFormula');
  }

  return {
    baseFormula: rule.baseFormula,
    conditions: rule.conditions || [],
    variables: rule.variables || [],
    priority: rule.priority || 0,
  };
}

/**
 * Get rule for component type
 * @param {string} category - Component category
 * @param {string} type - Component type
 * @param {object} customRules - Override rules
 * @returns {object} Rule object
 */
export function getRule(category, type, customRules = {}) {
  const allRules = { ...DEFAULT_RULES, ...customRules };
  try {
    PricingRulesValidator.validateRule(
      allRules[category]?.[type],
      category,
      type
    );
    return allRules[category][type];
  } catch (error) {
    return null;
  }
}

/**
 * Update rule for component type
 * @param {string} category - Component category
 * @param {string} type - Component type
 * @param {object} newRule - New rule
 */
export function updateRule(category, type, newRule) {
  if (!DEFAULT_RULES[category]) {
    DEFAULT_RULES[category] = {};
  }
  DEFAULT_RULES[category][type] = createRule(newRule);
}

/**
 * Add condition to rule
 * @param {string} category - Component category
 * @param {string} type - Component type
 * @param {object} condition - Condition to add
 */
export function addCondition(category, type, condition) {
  const rule = DEFAULT_RULES[category]?.[type];
  if (rule && PricingRulesValidator.isConditionSafe(condition.if)) {
    rule.conditions.push(condition);
  }
}

/**
 * Get all rules
 * @returns {object} All pricing rules
 */
export function getAllRules() {
  return JSON.parse(JSON.stringify(DEFAULT_RULES));
}

export default {
  DEFAULT_RULES,
  createRule,
  getRule,
  updateRule,
  addCondition,
  getAllRules,
};
